import type { AMIDataPoint } from '../types/project';
import { supabase } from './supabase';
import type { AmiPersistOp, AmiPersistResult } from './amiPersistStatus';
import { publishAmiPersistStatus } from './amiPersistStatus';

// Single gateway for every write to the ami_data_points table. All interval-row
// persistence — promote, AMI upload/replace, the mark-complete backstop, the
// on-load backfill, and deletion — routes through here.
//
// Two layers of protection against concurrent-write hazards:
//   1. In-flight guard (below): persists for the same project are SERIALIZED —
//      each caller chains behind any earlier in-flight write and then performs
//      its OWN write. It does NOT coalesce: a later call (e.g. Replace) is never
//      absorbed into an earlier call's write, so a superseding series is never
//      silently dropped. The guard's only job is ordering.
//   2. Atomic RPC (replace_ami_intervals): the DELETE + INSERT happen in ONE
//      transaction server-side, with a UNIQUE(project_id, timestamp) constraint
//      and DISTINCT ON (timestamp) de-duplication. Serialized writes are therefore
//      safe with last-write-wins semantics; interleaving is impossible.

// projectId -> the tail of the in-flight operation chain for that project.
const inFlight = new Map<string, Promise<AmiPersistResult>>();

/**
 * Run `operation` after any operation already in flight for the same project.
 *
 * CHAIN, don't coalesce. Read the tail of the chain, then build work that runs
 * AFTER it. The check-and-set is synchronous (nothing is awaited between the get
 * and the set), so it is atomic under JS's single thread — each caller chains
 * onto the exact operation that was in flight when it arrived, and two callers
 * can never both skip the chain. Each caller performs and returns the result of
 * its OWN operation; a later one is never absorbed into an earlier one's work.
 *
 * Callers MUST reach here without awaiting anything first, or their place in
 * the queue is decided by a scheduling accident rather than by call order. That
 * ordering is what keeps a Replace safe: the delete is registered before the
 * replacement upload's save is, so the save can never land first and then be
 * wiped by the delete it was meant to follow.
 */
function runSerialized(
  projectId: string,
  caller: string,
  operation: () => Promise<AmiPersistResult>
): Promise<AmiPersistResult> {
  const prior = inFlight.get(projectId);

  const work: Promise<AmiPersistResult> = (async () => {
    // Wait for any earlier operation to finish first — but never let its outcome
    // (rejection, or a 'failed' result) prevent this one from proceeding.
    if (prior) {
      console.log(
        `[amiPersistence] caller=${caller} project=${projectId} — ` +
        `awaiting prior operation before running.`
      );
      try { await prior; } catch { /* prior's failure must not block this one */ }
    }
    return operation();
  })();

  // Store BEFORE awaiting so a later caller chains onto THIS work.
  inFlight.set(projectId, work);

  return (async () => {
    try {
      return await work;
    } finally {
      // Only clear if we are still the tail of the chain — a newer call may have
      // already chained behind us and replaced the entry; do not clobber it.
      if (inFlight.get(projectId) === work) {
        inFlight.delete(projectId);
      }
    }
  })();
}

/**
 * Flatten a thrown value to a displayable message. Supabase rejects with a
 * PostgrestError-shaped plain object, NOT an Error instance — so an
 * `instanceof Error` check alone would fall through to String(error) and put
 * the literal text "[object Object]" in front of the user.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message: unknown };
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return String(error);
}

/**
 * Decide whether a failed write could ever succeed on a later attempt.
 *
 * UNRECOVERABLE means the project is gone from the server or is owned by
 * someone else. Every retry against that id will fail identically, forever, so
 * the banner must not offer one.
 *
 * Two independent signals, either of which is sufficient:
 *
 *   1. SQLSTATE 42501, which replace_ami_intervals raises on its ownership
 *      check as of migration 20260727120000, and which delete_ami_intervals has
 *      raised on the same check since it was written.
 *   2. The literal text 'not owned by caller' in the message.
 *
 * The message fallback is NOT redundant. Before that migration both of the
 * function's exceptions raised bare, arriving as the generic P0001, and the
 * migration does not reach every environment at the same moment. The client
 * has to classify correctly against the old function and the new one, so the
 * text check stays even after the rollout completes.
 */
function isUnrecoverable(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const { code } = error as { code: unknown };
    if (code === '42501') return true;
  }
  return errorMessage(error).includes('not owned by caller');
}

/**
 * Replace the persisted interval series for a project, atomically.
 *
 * Returns an AmiPersistResult describing the outcome in three distinguishable
 * states — the old boolean collapsed all three into `false`, which made a real
 * write failure impossible to tell apart from a deliberate no-op:
 *
 *   'ok'      — the RPC ran; `inserted` rows are now stored.
 *   'skipped' — nothing was attempted, and nothing is wrong. `reason` says which
 *               guard fired: 'local_id' for a falsy/local_ id (interval rows must
 *               never be stored against an un-promoted project), or 'empty_series'
 *               for a null/empty array (never delete rows on behalf of nothing).
 *   'failed'  — a write was attempted and did not land. `error` carries the
 *               message and `retry` re-runs the same write.
 *
 * COMPLETIONS ('ok' and 'failed') are published to the amiPersistStatus store,
 * which drives the hidden status element and the user-facing failure banner.
 * Skips are not completions and are never published; they carry seq 0.
 *
 * NEVER throws. Results compute from in-memory data regardless, so a persistence
 * failure must not block the user.
 *
 * @param caller short tag identifying the call site, so serialization is
 *               self-identifying in the logs and in the published result.
 */
export async function persistAmiIntervals(
  projectId: string,
  amiData: AMIDataPoint[],
  caller: string = 'unknown'
): Promise<AmiPersistResult> {
  // Never write interval rows against a local (un-promoted) id.
  if (!projectId || projectId.startsWith('local_')) {
    return { state: 'skipped', reason: 'local_id', projectId, caller, sent: 0, inserted: 0, seq: 0, recoverable: true };
  }

  // Never delete existing rows on behalf of an empty series.
  if (!amiData || amiData.length === 0) {
    return { state: 'skipped', reason: 'empty_series', projectId, caller, sent: 0, inserted: 0, seq: 0, recoverable: true };
  }

  // Match the RPC payload contract EXACTLY: array of { ts, kw } — key is "ts",
  // not "timestamp". ts is the ISO-ish timestamp string already stored in the
  // timestamptz column; kw is a number.
  const rows = amiData.map((point) => ({ ts: point.timestamp, kw: point.kw }));

  return runSerialized(projectId, caller, async () => {
    try {
      const { data, error } = await supabase.rpc('replace_ami_intervals', {
        p_project_id: projectId,
        p_rows: rows,
      });
      if (error) throw error;

      const inserted = typeof data === 'number' ? data : Number(data);
      console.log(
        `[persistAmiIntervals] caller=${caller} project=${projectId} ` +
        `sent=${rows.length} inserted=${inserted}`
      );
      // The RPC de-duplicates via DISTINCT ON (timestamp); a shortfall means the
      // in-memory series itself carried duplicate timestamps.
      if (Number.isFinite(inserted) && inserted !== rows.length) {
        console.warn(
          `[persistAmiIntervals] caller=${caller} project=${projectId} inserted ` +
          `${inserted} != sent ${rows.length} — payload contained duplicate timestamps.`
        );
      }
      // A completion — publish so observers (status element, banner) see it.
      return publishAmiPersistStatus({
        state: 'ok',
        op: 'save',
        projectId,
        caller,
        sent: rows.length,
        inserted,
        recoverable: true,
      });
    } catch (error) {
      console.error(
        `[persistAmiIntervals] caller=${caller} project=${projectId} rows=${rows.length} failed:`,
        error
      );
      // A failed write is ALSO a completion and must publish — this is the only
      // path by which a persistence failure becomes visible to the user.
      //
      // Classify BEFORE publishing. An unrecoverable failure publishes with NO
      // retry function at all, rather than a retry the banner is expected to
      // hide: absence of the function is the single source of truth, so no
      // other observer can offer a retry that cannot work.
      const unrecoverable = isUnrecoverable(error);
      return publishAmiPersistStatus({
        state: 'failed',
        op: 'save',
        projectId,
        caller,
        sent: rows.length,
        inserted: 0,
        error: errorMessage(error),
        recoverable: !unrecoverable,
        ...(unrecoverable
          ? {}
          : { retry: () => persistAmiIntervals(projectId, amiData, 'retry') }),
      });
    }
  });
}

/**
 * Erase the persisted interval series for a project, atomically.
 *
 * The counterpart to persistAmiIntervals, and the missing half of the Delete
 * Data button. Before this existed, deleting cleared React state and the
 * IndexedDB cache only: the server kept every row, so the next page load found
 * an empty cache, fell back to Supabase, and handed the "deleted" series
 * straight back. Delete has to reach the server or it is not a delete.
 *
 * Routes through the same in-flight chain as the save path, so a delete and a
 * save for one project can never interleave — which is exactly what Replace
 * does: delete the old series, then upload the new one.
 *
 * The RPC (delete_ami_intervals) removes the rows AND clears every projects
 * column derived from them in ONE transaction, so a project can never be left
 * with no readings but a peak demand still on record.
 *
 * Results mirror persistAmiIntervals:
 *
 *   'ok'      — the RPC ran; the project holds no interval rows.
 *   'skipped' — 'local_id' for a falsy or un-promoted id. There is nothing on
 *               the server to delete, and the caller's local clear is the whole
 *               job. Not a completion, never published.
 *   'failed'  — the delete was attempted and did not land. The rows are still
 *               there, and the banner has to say so.
 *
 * `sent` and `inserted` are both 0 on every delete: neither describes this
 * operation, and reporting a row count under a field named "inserted" would
 * read as rows written. The number of rows removed goes to the console.
 *
 * NEVER throws.
 */
export async function deleteAmiIntervals(
  projectId: string,
  caller: string = 'unknown'
): Promise<AmiPersistResult> {
  const op: AmiPersistOp = 'delete';

  // An un-promoted project has no server rows; the local clear is the whole job.
  if (!projectId || projectId.startsWith('local_')) {
    return { state: 'skipped', reason: 'local_id', op, projectId, caller, sent: 0, inserted: 0, seq: 0, recoverable: true };
  }

  return runSerialized(projectId, caller, async () => {
    try {
      const { data, error } = await supabase.rpc('delete_ami_intervals', {
        p_project_id: projectId,
      });
      if (error) throw error;

      const removed = typeof data === 'number' ? data : Number(data);
      console.log(
        `[deleteAmiIntervals] caller=${caller} project=${projectId} removed=${removed}`
      );
      return publishAmiPersistStatus({
        state: 'ok',
        op,
        projectId,
        caller,
        sent: 0,
        inserted: 0,
        recoverable: true,
      });
    } catch (error) {
      console.error(
        `[deleteAmiIntervals] caller=${caller} project=${projectId} failed:`,
        error
      );
      // Same classification as the save path — the RPC raises the same 42501 on
      // the same two ownership conditions, so a delete against a project that is
      // gone or belongs to someone else offers no retry either.
      const unrecoverable = isUnrecoverable(error);
      return publishAmiPersistStatus({
        state: 'failed',
        op,
        projectId,
        caller,
        sent: 0,
        inserted: 0,
        error: errorMessage(error),
        recoverable: !unrecoverable,
        ...(unrecoverable
          ? {}
          : { retry: () => deleteAmiIntervals(projectId, 'retry') }),
      });
    }
  });
}
